import { calculateCampaignDurationInDays } from '@/constants'
import { useJobillaOrgId } from '@/hooks'
import { logger } from '@/ui'
import { Dialog, DialogPanel } from '@headlessui/react'
import {
	campaignChannel,
	campaignChannelMethods,
	CustomFieldData,
	type SubmitCampaignForReviewData,
} from '@jobilla/nexus-sdk'
import confetti from 'canvas-confetti'
import { format } from 'date-fns'
import { useAtomValue } from 'jotai'
import { AnimatePresence, motion } from 'motion/react'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimateChangeInSize, Button, Jobibot } from '../../rapid-ui'
import { CampaignModel } from '../../views'
import { PreviewMode, PreviewPanel } from '../../views/campaign-card/overview/preview-panel'
import { CustomFields } from '../../views/settings/sub-pages/custom-fields/CustomFields'
import { CampaignChannel } from '../../views/shared/conformed/review'
import { completionHasErrorAtom, completionStepsAtom, feedAdAtom, storyAdAtom } from '../state'
import { AdCampaignCity } from './ad-campaign-location-selector'
import { BillingDrawer, BillingDrawerState } from './billing-drawer'
import { CompletionProgress } from './completion-progress'

export type CompletePopupRef = {
	open: () => void
	close: () => void
}

export type CompletePopupProps = {
	campaignId: string
	channels: CampaignChannel[]
	onRedirectAfterComplete: () => void
	onConfirm: (
		paymentMethods: SubmitCampaignForReviewData['paymentMethods'],
		stripePaymentMethodId?: string,
		profileId?: string | null,
		inventoryItems?: SubmitCampaignForReviewData['inventoryItems'],
	) => Promise<void>
	campaignTitle: string
	startDate: Date
	endDate: Date
	locations: AdCampaignCity[]
	customFields?: CustomFields
	customFieldsMetadata?: CustomFieldData[]
	renderPreviews: () => Promise<{ feedAdBlob: Blob; storyAdBlob: Blob }>
}

type PopupState = 'rendering' | 'confirm' | 'progress' | 'complete'

export const CompletePopup = forwardRef<CompletePopupRef, CompletePopupProps>(
	(
		{
			campaignId,
			channels,
			onRedirectAfterComplete,
			onConfirm,
			campaignTitle,
			startDate,
			endDate,
			locations,
			customFields,
			customFieldsMetadata,
			renderPreviews,
		},
		ref,
	) => {
		const { t } = useTranslation('editor')
		const hasMetaChannel = channels.includes(campaignChannel.META)
		const hasBillableChannel = channels.some((channel) => campaignChannelMethods.isBillable(channel))
		const [isOpen, setIsOpen] = useState(false)
		const [state, setState] = useState<PopupState>('rendering')
		const [feedAdDataUrl, setFeedAdDataUrl] = useState<string | null>(null)
		const [storyAdDataUrl, setStoryAdDataUrl] = useState<string | null>(null)
		const [previewMode, setPreviewMode] = useState<PreviewMode>('feed')

		const billingStateRef = useRef<BillingDrawerState | null>(null)

		const handleBillingStateChange = useCallback((next: BillingDrawerState) => {
			billingStateRef.current = next
		}, [])

		const feedAd = useAtomValue(feedAdAtom)
		const storyAd = useAtomValue(storyAdAtom)
		const completionSteps = useAtomValue(completionStepsAtom)
		const completionHasError = useAtomValue(completionHasErrorAtom)

		const organisationId = useJobillaOrgId()

		useImperativeHandle(ref, () => ({
			open: () => {
				setIsOpen(true)
				setState('rendering')
				setFeedAdDataUrl(null)
				setStoryAdDataUrl(null)
			},
			close: () => {
				setIsOpen(false)
				setState('rendering')
			},
		}))

		// Render previews when popup opens
		useEffect(() => {
			if (!isOpen || state !== 'rendering') {
				return
			}

			if (!hasMetaChannel) {
				setState('confirm')
				return
			}

			let cancelled = false

			const render = async () => {
				try {
					const { feedAdBlob, storyAdBlob } = await renderPreviews()

					if (cancelled) { return }

					// Convert blobs to object URLs
					const feedUrl = URL.createObjectURL(feedAdBlob)
					const storyUrl = URL.createObjectURL(storyAdBlob)

					setFeedAdDataUrl(feedUrl)
					setStoryAdDataUrl(storyUrl)
					setState('confirm')
				} catch (error) {
					logger.error(error, { tags: { component: 'CompletePopup', action: 'render-previews' } })
					if (!cancelled) {
						setState('confirm')
					}
				}
			}

			render()

			return () => {
				cancelled = true
			}
		}, [isOpen, state, hasMetaChannel, renderPreviews])

		// Cleanup object URLs when popup closes
		useEffect(() => {
			if (!isOpen) {
				if (feedAdDataUrl) {
					URL.revokeObjectURL(feedAdDataUrl)
					setFeedAdDataUrl(null)
				}
				if (storyAdDataUrl) {
					URL.revokeObjectURL(storyAdDataUrl)
					setStoryAdDataUrl(null)
				}
			}
		}, [isOpen])

		useEffect(() => {
			if (state === 'complete') {
				const colors = ['#b365f6', '#9a4bf5', '#872cf0', '#710ce2', '#cb87fb', '#f0cefd']
				const duration = 2500
				const end = Date.now() + duration

				const frame = () => {
					void confetti({
						particleCount: 4,
						colors,
						angle: 60,
						spread: 55,
						origin: { x: 0, y: 0.6 },
					})
					void confetti({
						particleCount: 4,
						colors,
						angle: 120,
						spread: 55,
						origin: { x: 1, y: 0.6 },
					})

					if (Date.now() < end) {
						requestAnimationFrame(frame)
					}
				}

				frame()
			}
		}, [state])

		const handleCancel = () => {
			logger.track('Editor: Review cancelled', {
				data: {
					campaign_id: campaignId,
					organisation_id: organisationId,
					review_step: state,
					preview_mode: previewMode,
				},
			})
			setIsOpen(false)
			setState('rendering')
		}

		const handleConfirm = async () => {
			const billing = billingStateRef.current
			logger.track('Editor: Submitted for review', {
				data: {
					campaign_id: campaignId,
					organisation_id: organisationId,
					payment_method: billing?.selected?.kind ?? null,
					requires_payment: billing?.requiresPayment ?? false,
					external_invoicing: billing?.externalInvoicing ?? false,
					amount_remaining_cents: billing?.amountRemainingCents ?? 0,
				},
			})
			const paymentMethods = billing?.paymentMethods ?? []
			const stripePaymentMethodId = billing?.selected?.kind === 'card' ? billing.selected.methodId : undefined
			const profileId = billing?.profileId ?? null
			const inventoryItems = billing?.inventoryItems ?? []

			// Run save → check → authorise → submit behind the progress view. Each
			// step's state is reflected live; on failure we stay on the progress
			// screen showing which step failed (and any issues) so the user can go
			// back and fix it. The card 3DS challenge, if any, surfaces during the
			// authorise step.
			setState('progress')
			try {
				await onConfirm(paymentMethods, stripePaymentMethodId, profileId, inventoryItems)
				setState('complete')
			} catch {
				// The failed step is already reflected in the progress view.
			}
		}

		const days = calculateCampaignDurationInDays(startDate, endDate)
		const dateRangeText = `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')} (${days} day${
			days !== 1 ? 's' : ''
		})`
		const locationsText = locations.map((c) => `${c.name}, ${c.country.name}`).join(' | ')

		// Create minimal campaign object for the preview component
		const campaign = {
			id: '',
			image: feedAdDataUrl ?? '',
			storyImage: storyAdDataUrl ?? '',
			title: campaignTitle,
		} as CampaignModel

		return (
			<AnimatePresence mode="popLayout">
				{isOpen && (
					<Dialog
						static={true}
						open={isOpen}
						onClose={() => {}}
						className="relative z-50"
					>
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="fixed inset-0 bg-black/40 backdrop-blur-sm"
						/>
						<div className="fixed inset-0 w-screen overflow-y-auto">
							<div className="flex min-h-full items-center justify-center p-4">
								<div className="flex items-center">
									<DialogPanel
										as={motion.div}
										initial={{
											opacity: 0,
											scale: 0.95,
											transition: { duration: 0.65, type: 'spring' },
										}}
										animate={{
											opacity: 1,
											scale: 1,
											transition: { duration: 0.65, type: 'spring' },
										}}
										exit={{
											opacity: 0,
											scale: 0.95,
											transition: { duration: 0.65, type: 'spring' },
										}}
										className="ring ring-grey-200/50 rounded-sm bg-white p-8 flex flex-col items-center justify-center relative z-10 shadow-[0_8px_24px_-4px_rgba(0,0,0,0.15),12px_0_24px_-6px_rgba(0,0,0,0.2)]"
									>
										<AnimateChangeInSize>
											<AnimatePresence mode="wait">
												{state === 'rendering' && (
													<motion.div
														key="rendering"
														initial={{ opacity: 0, y: 10 }}
														animate={{ opacity: 1, y: 0 }}
														exit={{ opacity: 0, y: -10 }}
														className="flex flex-col items-center gap-4"
													>
														<div className="relative flex items-center justify-center gap-2 w-24 h-24">
															<Jobibot size={48} />
															<div className="absolute inset-0 border-4 border-grey-100 border-t-brand-500 rounded-full animate-spin" />
														</div>

														<div className="text-lg text-grey-600 max-w-[360px] text-center">
															{t('completePopup.preparingPreview')}
														</div>
													</motion.div>
												)}

												{state === 'confirm' && (
													<motion.div
														key="confirm"
														initial={{ opacity: 0, y: 10 }}
														animate={{ opacity: 1, y: 0 }}
														exit={{ opacity: 0, y: -10 }}
														className="flex flex-col gap-6"
													>
														<h2 className="text-2xl font-semibold text-grey-900 text-center">
															{t('completePopup.confirmLaunch')}
														</h2>

														<div className="text-lg font-medium text-grey-800 text-center break-words max-w-[480px]">
															{campaignTitle}
														</div>

														{hasMetaChannel && (
															<div className="origin-top">
																<PreviewPanel
																	previewMode={previewMode}
																	onPrevious={() =>
																		setPreviewMode(
																			previewMode === 'feed' ? 'story' : 'feed',
																		)}
																	onNext={() =>
																		setPreviewMode(
																			previewMode === 'feed' ? 'story' : 'feed',
																		)}
																	campaign={campaign}
																	feedAd={feedAd}
																	storyAd={storyAd}
																	isLoading={false}
																/>
															</div>
														)}

														<div className="flex flex-col gap-2 text-sm text-grey-700">
															{hasMetaChannel && (
																<>
																	<div className="flex justify-between gap-8">
																		<span className="text-grey-500">
																			{t('completePopup.dates')}:
																		</span>
																		<span className="font-medium">
																			{dateRangeText}
																		</span>
																	</div>
																	<div className="flex justify-between gap-8">
																		<span className="text-grey-500">
																			{t('completePopup.targeting')}:
																		</span>
																		<span className="font-medium text-right">
																			{locationsText}
																		</span>
																	</div>
																</>
															)}
															{customFields && Object.keys(customFields).length > 0 && (
																<>
																	{customFieldsMetadata?.map((field) => (
																		<div
																			key={field.id}
																			className="flex justify-between gap-8"
																		>
																			<span className="text-grey-500">
																				{field.name}:
																			</span>
																			<span className="font-medium text-right">
																				{customFields[field.id] || '-'}
																			</span>
																		</div>
																	))}
																</>
															)}
														</div>

														{!hasMetaChannel && (
															<div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-center">
																<small className="text-grey-700">
																	{t('completePopup.careerSiteOnlyMessage')}
																</small>
															</div>
														)}

														<div className="flex items-center justify-between gap-3 pt-2">
															<Button
																type="subtle"
																size="massive"
																prefix="back"
																prefixAlign="left"
																animatePrefix="shake"
																onClick={handleCancel}
															>
																{t('billingDrawer.makeChanges')}
															</Button>
															{!hasBillableChannel && (
																<Button
																	type="committal"
																	size="massive"
																	onClick={handleConfirm}
																>
																	{t('billingDrawer.launch')}
																</Button>
															)}
														</div>
													</motion.div>
												)}

												{state === 'progress' && (
													<motion.div
														key="progress"
														initial={{ opacity: 0, y: 10 }}
														animate={{ opacity: 1, y: 0 }}
														exit={{ opacity: 0, y: -10 }}
														className="flex flex-col gap-6"
													>
														<h2 className="text-xl font-semibold text-grey-900 text-center">
															{completionHasError
																? t('completionProgress.failedTitle')
																: t('completionProgress.title')}
														</h2>

														<CompletionProgress steps={completionSteps} />

														{completionHasError && (
															<div className="flex justify-start pt-2">
																<Button
																	type="subtle"
																	size="massive"
																	prefix="back"
																	prefixAlign="left"
																	animatePrefix="shake"
																	onClick={() => setState('confirm')}
																>
																	{t('completionProgress.back')}
																</Button>
															</div>
														)}
													</motion.div>
												)}

												{state === 'complete' && (
													<motion.div
														key="complete"
														initial={{ opacity: 0, y: 10 }}
														animate={{ opacity: 1, y: 0 }}
														exit={{ opacity: 0, y: -10 }}
														className="flex flex-col items-center gap-6"
													>
														<div className="text-4xl font-thin tracking-wide text-brand-700 max-w-[400px] text-center">
															{t('completePopup.thankYou')}
														</div>

														<div className="text-lg text-grey-600 max-w-[360px] text-center">
															{t('completePopup.reviewAndLaunch')}
														</div>
														<Button
															type="committal"
															onClick={() => {
																setIsOpen(false)
																onRedirectAfterComplete()
															}}
														>
															{t('completePopup.backToDashboard')}
														</Button>
													</motion.div>
												)}
											</AnimatePresence>
										</AnimateChangeInSize>
									</DialogPanel>

									<AnimatePresence>
										{state === 'confirm' && hasBillableChannel && (
											<motion.div
												key="billing-drawer"
												initial={{ opacity: 0, x: -40 }}
												animate={{
													opacity: 1,
													x: 0,
													transition: { duration: 0.45, type: 'spring', delay: 0.15 },
												}}
												exit={{ opacity: 0, x: -40, transition: { duration: 0.2 } }}
												className="-ml-px my-8"
											>
												<BillingDrawer
													campaignId={campaignId}
													channels={channels}
													onStateChange={handleBillingStateChange}
													onConfirm={handleConfirm}
												/>
											</motion.div>
										)}
									</AnimatePresence>
								</div>
							</div>
						</div>
					</Dialog>
				)}
			</AnimatePresence>
		)
	},
)

CompletePopup.displayName = 'CompletePopup'
